/**
 * Keep an asked question BELOW the cards that landed after it.
 *
 * A card is drawn ABOVE the words about it — the reading order every surface already uses for the cards
 * that RIDE a reply (`MessageRow` draws opportunity, outreach preview, credits and connector above the
 * prose, chips underneath). The cards that are their OWN row — the agent-join orbit, a milestone, an
 * agent at work, a credit gift — normally land above the reply too, because the tool that produced one
 * persists it inline, before the model writes the announcement (`persistCard`).
 *
 * The case that escapes it is a card row landing AFTER the question: the tool ran in the same iteration
 * whose text block was already streamed, or the card arrived from a background task minutes later. The
 * transcript then ends with two full-bleed cards and the ask — "Would you like to add your number?" —
 * sits off the top of the viewport, so the reader gets chips whose question they never read.
 *
 * Display order only, and a pure function of the list, so history and the live turn agree and the
 * question does not snap back once it is answered.
 *
 * Deliberately narrow:
 *  - only a row carrying `choices` moves. That IS the question — a reply the reader has to act on —
 *    and it keeps an ordinary reply that happens to precede a late card exactly where it was written.
 *  - only own-row cards move it (`hasPresentationCard`). A card that rides a reply is already above
 *    that reply's own words and has nothing to reorder.
 *
 * ⚠️ Anything that means "newest" — the streaming caret's tail, a card's entrance animation on the
 * phones — must read the ARRIVAL order, not this list: the reorder can leave a settled ask last.
 *
 * Mirrored by iOS `ChatMessageListPolicy.questionAfterCards` and Android `questionAfterCards`.
 */
import { hasPresentationCard } from './chat-cards';

/** The minimal row shape the reorder reads: an optional ask, plus the own-row card markers. */
export type QuestionOrderRow = {
  choices?: unknown;
  agentCard?: unknown;
  agentAtWorkCard?: unknown;
  milestoneCard?: unknown;
  creditGiftCard?: unknown;
};

/** True for a row that IS a card — one the transcript draws instead of a bubble. */
function isCardRow(m: QuestionOrderRow): boolean {
  return hasPresentationCard(m as never);
}

/**
 * Move every `choices`-carrying row below the run of own-row cards directly after it. Returns the input
 * array unchanged (same identity) when nothing moves, so the common case costs one scan.
 *
 * @example
 * questionAfterCards([{ choices: ask }, { milestoneCard }, { agentCard }]);
 * // => [{ milestoneCard }, { agentCard }, { choices: ask }]
 *
 * @example
 * questionAfterCards([{ agentCard }, { choices: ask }]); // => the same array, untouched
 */
export function questionAfterCards<M extends QuestionOrderRow>(messages: M[]): M[] {
  const moves = messages.some((m, i) => m.choices && !isCardRow(m) && !!messages[i + 1] && isCardRow(messages[i + 1]));
  if (!moves) return messages;

  const out: M[] = [];
  let i = 0;
  while (i < messages.length) {
    const m = messages[i];
    if (m.choices && !isCardRow(m) && messages[i + 1] && isCardRow(messages[i + 1])) {
      let j = i + 1;
      while (j < messages.length && isCardRow(messages[j])) j += 1;
      for (let k = i + 1; k < j; k += 1) out.push(messages[k]);
      out.push(m);
      i = j;
    } else {
      out.push(m);
      i += 1;
    }
  }
  return out;
}
